const API_URL = process.env.NEXT_PUBLIC_API_URL || ''

// Backend /api/market endpoint types

export interface IndexQuote {
  symbol: string
  name: string
  date: string
  open: number
  high: number
  low: number
  close: number
  previousClose: number | null
  volume: number | null
  diff: number
  changePercent: number
  monthly: number | null
  yearly: number | null
  currency: string | null
}

export interface ExchangeData {
  micCode: string
  name: string
  countryCode: string
  countryName: string | null
  currency: string
  indices: IndexQuote[]
}

export interface MarketRegionData {
  region: string
  name: string
  exchanges: ExchangeData[]
}

export async function fetchMarketOverview(): Promise<MarketRegionData[]> {
  try {
    const res = await fetch(`${API_URL}/api/market`, {
      next: { revalidate: 30 },
    })
    if (!res.ok) return []
    return await res.json()
  } catch (err) {
    console.warn('[market] fetch failed (transient):', err)
    return []
  }
}

export async function fetchQuote(symbol: string): Promise<IndexQuote | null> {
  try {
    const res = await fetch(`${API_URL}/api/market/quote/${symbol}`)
    if (!res.ok) return null
    return await res.json()
  } catch (err) {
    console.warn(`[quote ${symbol}] fetch failed:`, err)
    return null
  }
}

export interface ChartPoint {
  time: string
  open: number
  high: number
  low: number
  close: number
  volume: number | null
}

export interface ChartData {
  symbol: string
  count: number
  data: ChartPoint[]
}

export interface ExchangeDetail {
  micCode: string
  name: string
  countryCode: string
  countryName: string | null
  currency: string
  timezone: string
  tradingDays: string | null
  tradingHours: string | null
  dataAccess: string
  indices: IndexQuote[]
  stocks: IndexQuote[]
}

export async function fetchExchangeDetail(micCode: string): Promise<ExchangeDetail | null> {
  try {
    const res = await fetch(`${API_URL}/api/market/exchange/${micCode}`)
    if (!res.ok) return null
    return await res.json()
  } catch (err) {
    console.warn(`[exchange ${micCode}] fetch failed:`, err)
    return null
  }
}

export interface StockListResponse {
  items: IndexQuote[]
  totalCount: number
  page: number
  pageSize: number
}

export async function fetchStocksByExchange(
  exchanges: string[],
  page = 1,
  pageSize = 30,
  search?: string,
): Promise<StockListResponse> {
  try {
    let url = `${API_URL}/api/market/stocks?exchanges=${exchanges.join(',')}&page=${page}&pageSize=${pageSize}`
    if (search) url += `&search=${encodeURIComponent(search)}`
    const res = await fetch(url)
    if (!res.ok) return { items: [], totalCount: 0, page, pageSize }
    return await res.json()
  } catch {
    return { items: [], totalCount: 0, page, pageSize }
  }
}

export async function fetchQuotes(symbols: string[]): Promise<IndexQuote[]> {
  try {
    const res = await fetch(`${API_URL}/api/market/quotes?symbols=${symbols.join(',')}`)
    if (!res.ok) return []
    return await res.json()
  } catch {
    return []
  }
}

export async function fetchChartData(symbol: string, period?: string): Promise<ChartData | null> {
  try {
    const params = period ? `?period=${period}` : ''
    const res = await fetch(`${API_URL}/api/market/chart/${symbol}${params}`)
    if (!res.ok) return null
    return await res.json()
  } catch (err) {
    console.warn(`[chart ${symbol}] fetch failed:`, err)
    return null
  }
}
